import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import {
  ArrowRight, Play, Camera, Link2, FileText, MessageSquareText, FileType2,
  Upload, ScanSearch, ShieldCheck, User, Globe, Clock, CreditCard,
  UserRoundX, KeyRound, Fingerprint, Layers, Lock, Database, ServerCog, EyeOff,
  ShieldAlert,
} from "lucide-react";

const Eyebrow = ({ children }) => (
  <span className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-700">{children}</span>
);

const HeroPreview = () => {
  const { t } = useLang();
  return (
    <div className="relative">
      <div className="absolute -inset-4 rounded-[2rem] bg-blue-50 blur-2xl" />
      <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl">
        <div className="flex items-center gap-2 border-b border-slate-200 px-5 py-3">
          <div className="flex gap-1.5">
            <span className="h-3 w-3 rounded-full bg-rose-500/70" />
            <span className="h-3 w-3 rounded-full bg-amber-500/70" />
            <span className="h-3 w-3 rounded-full bg-emerald-500/70" />
          </div>
          <span className="ml-2 font-mono text-xs text-slate-500">trustwall · scan</span>
        </div>

        <div className="relative p-5">
          <div className="pointer-events-none absolute inset-x-5 top-16 z-10 h-16 bg-gradient-to-b from-rose-500/20 to-transparent tw-scan-line" />
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">{t("home.hero.previewTitle")}</p>
          <div className="mt-3 rounded-2xl border border-slate-200 bg-white p-4">
            <p className="text-sm leading-relaxed text-slate-600">{t("home.hero.previewMsg")}</p>
          </div>

          <div className="mt-4 flex items-center gap-3 rounded-2xl border border-rose-500/30 bg-rose-500/10 p-4">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-rose-500/15">
              <ShieldAlert className="h-6 w-6 text-rose-400" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-lg font-bold text-rose-400">{t("common.danger")}</span>
                <span className="font-mono text-sm font-bold text-rose-300">88</span>
              </div>
              <p className="text-xs text-slate-500">{t("home.hero.previewVerdict")}</p>
            </div>
          </div>

          <div className="mt-3 grid grid-cols-2 gap-2">
            {[t("home.hero.previewSignal1"), t("home.hero.previewSignal2")].map((sig) => (
              <div key={sig} className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500">
                {sig}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default function Home() {
  const { t, lang } = useLang();

  const guideLang = lang === "fr" ? "fr" : "nl";
  const homeGuides = [
    { label: { nl: "Phishing herkennen", fr: "Reconnaître le phishing" }, href: { nl: "/gids/phishing-herkennen.html", fr: "/fr/guide/reconnaitre-le-phishing.html" } },
    { label: { nl: "Is deze link veilig?", fr: "Ce lien est-il sûr ?" }, href: { nl: "/gids/link-controleren.html", fr: "/fr/guide/verifier-un-lien.html" } },
    { label: { nl: "Vals pakketbericht herkennen", fr: "Faux message de colis" }, href: { nl: "/gids/vals-pakketbericht.html", fr: "/fr/guide/faux-message-de-colis.html" } },
    { label: { nl: "Valse bankmail & itsme", fr: "Faux mail bancaire & itsme" }, href: { nl: "/gids/valse-bankmail-itsme.html", fr: "/fr/guide/faux-mail-bancaire-itsme.html" } },
    { label: { nl: "Op een phishinglink geklikt?", fr: "Cliqué sur un lien ?" }, href: { nl: "/gids/geklikt-op-phishinglink.html", fr: "/fr/guide/clique-sur-un-lien-phishing.html" } },
    { label: { nl: "Verdacht bericht melden", fr: "Signaler un message suspect" }, href: { nl: "/gids/verdacht-bericht-melden.html", fr: "/fr/guide/signaler-un-message-suspect.html" } },
  ];
  const guideCopy = {
    nl: { title: "Gidsen: oplichting en phishing herkennen", intro: "Leer verdachte links, sms-berichten, e-mails, valse pakketberichten (bpost, DHL) en bankfraude herkennen. Onze gratis gidsen leggen stap voor stap uit hoe je phishing herkent en wat je doet als je op een valse link hebt geklikt.", all: "Alle gidsen bekijken" },
    fr: { title: "Guides : reconnaître les arnaques et le phishing", intro: "Apprenez à reconnaître les liens suspects, SMS, e-mails, faux messages de colis (bpost, DHL) et la fraude bancaire. Nos guides gratuits expliquent étape par étape comment repérer le phishing et quoi faire si vous avez cliqué sur un faux lien.", all: "Voir tous les guides" },
    en: { title: "Guides: spot scams and phishing", intro: "Learn to recognise suspicious links, text messages, emails, fake parcel messages (bpost, DHL) and bank fraud. Our free guides explain step by step how to spot phishing and what to do if you clicked a fake link.", all: "View all guides" },
  };
  const gc = guideCopy[lang] || guideCopy.nl;

  const whatIcons = [Camera, Link2, FileText, MessageSquareText, FileType2];
  const howIcons = [Upload, ScanSearch, ShieldCheck];
  const whyIcons = [User, Globe, Clock, CreditCard, UserRoundX, KeyRound, Fingerprint, Layers];
  const privacyIcons = [Lock, Database, ServerCog, EyeOff];

  const whatItems = t("home.what.items");
  const howSteps = t("home.how.steps");
  const whyItems = t("home.why.items");
  const privacyPoints = t("home.privacy.points");
  const understand = t("home.understand");

  return (
    <div>
      {/* HERO */}
      <section className="tw-grid-bg relative overflow-hidden pt-28 pb-20 sm:pt-36 lg:pt-40">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:gap-10 lg:px-8">
          <div className="tw-fade-up">
            <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-4 py-1.5 text-xs font-medium text-slate-600">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> {t("home.hero.badge")}
            </div>
            <h1 className="mt-6 text-4xl font-bold leading-[1.1] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
              {t("home.hero.title")}
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg">
              {t("home.hero.subtitle")}
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/check"
                data-testid="hero-primary-cta"
                className="tw-pulse group inline-flex items-center justify-center gap-2 rounded-full bg-[#1E3A8A] px-6 py-3.5 text-base font-semibold text-white transition-all hover:bg-[#1D4ED8]"
              >
                {t("home.hero.primary")}
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5" />
              </Link>
              <Link
                to="/check#demo"
                data-testid="hero-secondary-demo-cta"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-6 py-3.5 text-base font-semibold text-slate-900 transition-colors hover:bg-slate-100"
              >
                <Play className="h-4 w-4" /> {t("home.hero.secondary")}
              </Link>
            </div>
          </div>
          <div className="tw-fade-up" style={{ animationDelay: "0.15s" }}>
            <HeroPreview />
          </div>
        </div>
      </section>

      {/* 10-SECOND UNDERSTANDING */}
      <section className="border-y border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
          <div className="grid gap-6 sm:grid-cols-3">
            {[
              { n: "1", t: understand.a, d: understand.aDesc },
              { n: "2", t: understand.b, d: understand.bDesc },
              { n: "3", t: understand.c, d: understand.cDesc },
            ].map((x) => (
              <div key={x.n} className="flex items-start gap-4">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-blue-200 bg-blue-50 font-mono text-sm font-bold text-blue-700">
                  {x.n}
                </span>
                <div>
                  <p className="font-semibold text-slate-900">{x.t}</p>
                  <p className="text-sm text-slate-500">{x.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT CAN YOU CHECK */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
        <div className="max-w-2xl">
          <Eyebrow>01 — Input</Eyebrow>
          <h2 className="mt-3 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">{t("home.what.title")}</h2>
          <p className="mt-3 text-base text-slate-500">{t("home.what.subtitle")}</p>
        </div>
        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {whatItems.map((item, i) => {
            const Icon = whatIcons[i];
            return (
              <div
                key={i}
                data-testid={`what-check-card-${i}`}
                className="group rounded-3xl border border-slate-200 bg-slate-50 p-6 transition-all hover:border-blue-200 hover:bg-slate-100"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 ring-1 ring-blue-200 transition-colors group-hover:bg-[#1D4ED8]/20">
                  <Icon className="h-6 w-6 text-blue-700" />
                </div>
                <h3 className="mt-4 text-lg font-semibold text-slate-900">{item.t}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-500">{item.d}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="border-y border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <div className="mx-auto max-w-2xl text-center">
            <Eyebrow>02 — Proces</Eyebrow>
            <h2 className="mt-3 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">{t("home.how.title")}</h2>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {howSteps.map((step, i) => {
              const Icon = howIcons[i];
              return (
                <div key={i} className="relative rounded-3xl border border-slate-200 bg-white p-7">
                  <span className="font-mono text-5xl font-bold text-slate-900/[0.06]">0{i + 1}</span>
                  <div className="mt-2 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 ring-1 ring-blue-200">
                    <Icon className="h-6 w-6 text-blue-700" />
                  </div>
                  <h3 className="mt-4 text-lg font-semibold text-slate-900">{step.t}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-500">{step.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY TRUSTWALL */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
        <div className="max-w-2xl">
          <Eyebrow>03 — Signalen</Eyebrow>
          <h2 className="mt-3 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">{t("home.why.title")}</h2>
          <p className="mt-3 text-base text-slate-500">{t("home.why.subtitle")}</p>
        </div>
        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {whyItems.map((item, i) => {
            const Icon = whyIcons[i];
            return (
              <div key={i} className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                <Icon className="h-5 w-5 text-blue-700" />
                <h3 className="mt-3 text-base font-semibold text-slate-900">{item.t}</h3>
                <p className="mt-1 text-sm leading-relaxed text-slate-500">{item.d}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* PRIVACY */}
      <section className="border-y border-slate-200 bg-gradient-to-b from-sky-500/[0.04] to-transparent">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
            <div>
              <Eyebrow>04 — Privacy</Eyebrow>
              <h2 className="mt-3 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">{t("home.privacy.title")}</h2>
              <p className="mt-3 max-w-lg text-base text-slate-500">{t("home.privacy.subtitle")}</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {privacyPoints.map((p, i) => {
                const Icon = privacyIcons[i];
                return (
                  <div key={i} className="rounded-2xl border border-slate-200 bg-white p-5">
                    <Icon className="h-5 w-5 text-emerald-400" />
                    <h3 className="mt-3 text-base font-semibold text-slate-900">{p.t}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-slate-500">{p.d}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* DEMO */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
        <div className="relative overflow-hidden rounded-[2rem] border border-slate-200 bg-white p-8 sm:p-12">
          <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-blue-50 blur-3xl" />
          <div className="relative max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-blue-700">
              {t("common.demo")}
            </span>
            <h2 className="mt-4 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">{t("home.demo.title")}</h2>
            <p className="mt-3 text-base text-slate-500">{t("home.demo.subtitle")}</p>
            <Link
              to="/check#demo"
              data-testid="home-demo-cta"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#1E3A8A] px-6 py-3 text-base font-semibold text-white transition-colors hover:bg-[#1D4ED8]"
            >
              <Play className="h-4 w-4" /> {t("home.demo.cta")}
            </Link>
            <p className="mt-3 text-xs text-slate-500">{t("home.demo.note")}</p>
          </div>
        </div>
      </section>

      {/* GUIDES (SEO) */}
      <section className="mx-auto max-w-7xl px-4 pb-4 sm:px-6 lg:px-8" data-testid="home-guides">
        <div className="rounded-[2rem] border border-slate-200 bg-slate-50 p-8 sm:p-12">
          <Eyebrow>{guideLang === "fr" ? "Guides" : "Gidsen"}</Eyebrow>
          <h2 className="mt-3 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">{gc.title}</h2>
          <p className="mt-3 max-w-3xl text-base leading-relaxed text-slate-500">{gc.intro}</p>
          <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {homeGuides.map((g, i) => (
              <a
                key={i}
                href={g.href[guideLang]}
                data-testid={`home-guide-link-${i}`}
                className="group flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4 text-sm font-semibold text-slate-700 transition-colors hover:border-blue-300 hover:text-blue-700"
              >
                {g.label[guideLang]}
                <ArrowRight className="h-4 w-4 shrink-0 text-slate-300 transition-colors group-hover:text-blue-700" />
              </a>
            ))}
          </div>
          <a
            href={guideLang === "fr" ? "/fr/guide/" : "/gids/"}
            data-testid="home-guides-all"
            className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-blue-700 hover:underline"
          >
            {gc.all} <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8">
        <div className="rounded-[2rem] border border-blue-200 bg-gradient-to-br from-sky-500/[0.08] to-transparent p-10 text-center sm:p-16">
          <h2 className="mx-auto max-w-2xl text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">{t("home.finalCta.title")}</h2>
          <p className="mt-4 text-base text-slate-500">{t("home.finalCta.subtitle")}</p>
          <Link
            to="/check"
            data-testid="home-final-cta"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#1E3A8A] px-8 py-4 text-base font-semibold text-white transition-all hover:bg-[#1D4ED8] hover:shadow-[0_0_32px_-4px_rgba(56,189,248,0.8)]"
          >
            {t("home.finalCta.button")} <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
